/**
 * Wraps a lazy import so a failed chunk fetch retries once before propagating.
 *
 * Prevents "Failed to fetch dynamically imported module" caused by a transient
 * network error or a CDN edge that is briefly stale after a deploy. If the chunk
 * genuinely no longer exists on the server, the retry still fails and the error
 * propagates to the global handler in app.component.ts, which performs a
 * one-time reload to pick up the fresh index.html.
 *
 * Usage in routing:
 *   loadChildren: () => loadWithRetry(() =>
 *     import('./x/x.module').then((m) => m.XModule))
 *
 *   loadComponent: () => loadWithRetry(() =>
 *     import('./x/x.component').then((m) => m.XComponent))
 */
export function loadWithRetry<T>(loader: () => Promise<T>, retries = 1): Promise<T> {
  return loader().catch((error: unknown) => {
    if (!isChunkLoadError(error) || retries <= 0) {
      throw error;
    }


    return new Promise<T>((resolve) => {
      setTimeout(resolve, 1000);
    }).then(() => loadWithRetry(loader, retries - 1));
  });
}


function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error? error.message: String(error);

  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Loading chunk .* failed/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /ChunkLoadError/i.test(message)
  );
}